import { theme } from "antd";
import { useColorMode } from "../../contexts/color-mode";
import { hexToRgba } from "./colorUtils";

export interface StatusConfig {
  bg: string;
  color: string;
  border: string;
}

/**
 * Hook that provides theme-aware styles for pages
 * @param statusConfig - Optional status configuration for status-specific styles
 * @returns Object containing all style definitions
 */
export const usePageStyles = (statusConfig?: StatusConfig) => {
  const { mode } = useColorMode();
  const isDark = mode === 'dark';
  const { token } = theme.useToken();
  const primaryColor = token.colorPrimary;
  const primaryRgba = (alpha: number) => hexToRgba(primaryColor, alpha);
  const successRgba = (alpha: number) => hexToRgba(token.colorSuccess, alpha);
  const warningRgba = (alpha: number) => hexToRgba(token.colorWarning, alpha);
  const infoRgba = (alpha: number) => hexToRgba(token.colorInfo, alpha);
  const errorRgba = (alpha: number) => hexToRgba(token.colorError, alpha);

  // Default status config if not provided (theme tokens)
  const defaultStatusConfig: StatusConfig = {
    bg: token.colorFillQuaternary,
    color: token.colorTextTertiary,
    border: token.colorBorderSecondary,
  };
  const finalStatusConfig = statusConfig || defaultStatusConfig;

  return {
    container: {
      fontFamily: "'DM Sans', -apple-system, sans-serif",
      color: isDark ? '#e8e8ed' : '#1e293b',
      padding: 0,
      position: 'relative' as const,
      overflow: 'hidden'
    },
    ambientGlow: {
      position: 'absolute' as const,
      top: '-100px',
      right: '-100px',
      width: '500px',
      height: '500px',
      background: isDark
        ? `radial-gradient(circle, ${primaryRgba(0.08)} 0%, transparent 70%)`
        : `radial-gradient(circle, ${primaryRgba(0.05)} 0%, transparent 70%)`,
      pointerEvents: 'none' as const,
      zIndex: 0
    },
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    breadcrumb: {
      fontSize: '13px',
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      letterSpacing: '0.5px'
    },
    breadcrumbActive: {
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)'
    },
    headerButton: {
      background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
      borderRadius: '10px',
      padding: '10px 18px',
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
      fontSize: '13px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      transition: 'all 0.2s ease'
    },
    primaryButton: {
      background: `linear-gradient(135deg, ${primaryColor} 0%, ${primaryColor}dd 100%)`,
      border: 'none',
      borderRadius: '10px',
      padding: '10px 20px',
      color: token.colorTextLightSolid,
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      boxShadow: `0 4px 20px ${primaryRgba(0.3)}`,
      transition: 'all 0.2s ease'
    },
    main: {
      position: 'relative' as const,
      zIndex: 1
    },
    heroCard: {
      background: isDark
        ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)'
        : 'linear-gradient(135deg, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0.6) 100%)',
      borderRadius: '24px',
      padding: '32px 40px',
      marginBottom: '32px',
      border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.06)',
      boxShadow: isDark ? 'none' : '0 4px 24px rgba(0,0,0,0.06)',
      backdropFilter: isDark ? 'none' : 'blur(20px)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      position: 'relative' as const,
      overflow: 'hidden'
    },
    heroGlow: {
      position: 'absolute' as const,
      right: '-50px',
      top: '-50px',
      width: '200px',
      height: '200px',
      background: isDark
        ? `radial-gradient(circle, ${primaryRgba(0.1)} 0%, transparent 70%)`
        : `radial-gradient(circle, ${primaryRgba(0.08)} 0%, transparent 70%)`,
      borderRadius: '50%'
    },
    avatar: {
      width: '72px',
      height: '72px',
      borderRadius: '18px',
      background: isDark
        ? `linear-gradient(135deg, ${primaryRgba(0.15)} 0%, ${primaryRgba(0.25)} 100%)`
        : `linear-gradient(135deg, ${primaryRgba(0.1)} 0%, ${primaryRgba(0.2)} 100%)`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '24px',
      fontWeight: 600,
      color: primaryColor,
      border: `1px solid ${primaryRgba(0.2)}`,
      boxShadow: isDark ? 'inset 0 1px 0 rgba(255,255,255,0.05)' : `0 2px 8px ${primaryRgba(0.15)}`
    },
    statusTag: {
      background: finalStatusConfig.bg,
      color: finalStatusConfig.color,
      padding: '6px 14px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      border: `1px solid ${finalStatusConfig.border}`
    },
    statusDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: finalStatusConfig.color,
      boxShadow: `0 0 8px ${finalStatusConfig.color}`
    },
    typeText: {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      fontSize: '13px'
    },
    metaLabel: {
      fontSize: '11px',
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      marginBottom: '4px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    metaValue: {
      fontSize: '14px',
      fontWeight: 500,
      color: isDark ? '#e8e8ed' : '#1e293b'
    },
    tabContainer: {
      display: 'flex',
      gap: '4px',
      marginBottom: '32px',
      background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      padding: '4px',
      borderRadius: '14px',
      width: 'fit-content',
      border: isDark ? '1px solid rgba(255,255,255,0.04)' : '1px solid rgba(0,0,0,0.04)'
    },
    tab: (isActive: boolean) => ({
      background: isActive 
        ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.9)') 
        : 'transparent',
      border: 'none',
      borderRadius: '10px',
      padding: '10px 24px',
      color: isActive 
        ? (isDark ? '#fff' : '#1e293b') 
        : (isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'),
      fontSize: '13px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: isActive && !isDark ? '0 2px 8px rgba(0,0,0,0.08)' : 'none'
    }),
    card: {
      background: isDark
        ? 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)'
        : token.colorBgElevated,
      borderRadius: '20px',
      padding: '28px',
      border: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.10)',
      boxShadow: isDark ? 'none' : '0 2px 12px rgba(0,0,0,0.08)',
      backdropFilter: isDark ? 'none' : 'blur(10px)',
      transition: 'all 0.3s ease'
    },
    /** Mobile card: transparent bg, bottom border only, no rounded corners, reduced padding */
    cardMobile: {
      background: 'transparent',
      borderRadius: 0,
      border: 'none',
      borderBottom: isDark ? '1px solid rgba(255,255,255,0.06)' : '1px solid rgba(0,0,0,0.10)',
      boxShadow: 'none',
      backdropFilter: 'none',
      padding: '14px 12px',
    },
    rowBackground: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
    /** Card style for stepped/wizard layouts (gradient, subtle border) */
    stepperCard: isDark
      ? {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.01) 100%)',
          border: '1px solid rgba(255,255,255,0.06)',
          boxShadow: 'none',
          backdropFilter: 'none'
        }
      : {
          background: 'linear-gradient(135deg, rgba(255,255,255,0.9) 0%, rgba(255,255,255,0.7) 100%)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          backdropFilter: 'blur(10px)'
        },
    /** Card style for selectable items (flat background, light shadow) */
    selectableCard: isDark
      ? {
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'none',
          backdropFilter: 'none'
        }
      : {
          background: 'rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          backdropFilter: 'blur(10px)'
        },
    /** Inner card / summary block (same as selectableCard) */
    summaryInnerCard: isDark
      ? {
          background: 'rgba(255,255,255,0.02)',
          border: '1px solid rgba(255,255,255,0.08)',
          boxShadow: 'none',
          backdropFilter: 'none'
        }
      : {
          background: 'rgba(0,0,0,0.02)',
          border: '1px solid rgba(0,0,0,0.06)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
          backdropFilter: 'blur(10px)'
        },
    /** Divider border color for summary sections */
    summaryDividerColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
    cardIcon: (color: string) => ({
      width: '40px',
      height: '40px',
      borderRadius: '12px',
      background:
        color === 'indigo'
          ? primaryRgba(0.1)
          : color === 'pink'
            ? warningRgba(0.1)
            : color === 'emerald'
              ? successRgba(0.1)
              : infoRgba(0.1),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }),
    cardTitle: {
      margin: 0,
      fontSize: '15px',
      fontWeight: 600,
      color: isDark ? '#e8e8ed' : '#1e293b'
    },
    label: {
      fontSize: '11px',
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      marginBottom: '6px',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.5px'
    },
    emptyText: {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)'
    },
    phoneLink: {
      color: isDark ? '#e8e8ed' : '#1e293b',
      textDecoration: 'none',
      fontSize: '15px',
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center',
      gap: '8px'
    },
    callBadge: {
      background: primaryRgba(0.1),
      padding: '4px 8px',
      borderRadius: '6px',
      fontSize: '10px',
      color: primaryColor
    },
    addressText: {
      fontSize: '15px',
      lineHeight: 1.7,
      color: isDark ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
    },
    addressMuted: {
      color: isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.4)',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      marginTop: '4px'
    },
    legalIdBadge: {
      color: isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.4)',
      fontSize: '12px',
      fontFamily: 'monospace',
      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.04)',
      padding: '4px 10px',
      borderRadius: '6px'
    },
    checkBox: (checked: boolean) => ({
      width: '20px',
      height: '20px',
      borderRadius: '6px',
      background: checked
        ? successRgba(0.2)
        : isDark
          ? 'rgba(255,255,255,0.05)'
          : 'rgba(0,0,0,0.05)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    }),
    infoRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '12px 16px',
      background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
      borderRadius: '12px'
    },
    infoRowLabel: {
      fontSize: '13px',
      color: isDark ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.5)'
    },
    infoRowValue: {
      fontSize: '15px',
      fontWeight: 600,
      color: isDark ? '#e8e8ed' : '#1e293b'
    },
    billingBadge: {
      background: primaryRgba(0.1),
      color: primaryColor,
      padding: '4px 12px',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: 500
    },
    quickActionButton: {
      background: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.8)',
      border: isDark ? '1px solid rgba(255,255,255,0.08)' : '1px solid rgba(0,0,0,0.08)',
      borderRadius: '12px',
      padding: '14px 24px',
      color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
      fontSize: '13px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      transition: 'all 0.2s ease',
      boxShadow: isDark ? 'none' : '0 2px 8px rgba(0,0,0,0.04)'
    },
    dangerButton: {
      borderColor: errorRgba(0.3),
      color: token.colorError,
    },
    semantic: {
      success: token.colorSuccess,
      warning: token.colorWarning,
      error: token.colorError,
      info: token.colorInfo,
    },
    // Expose primary color and rgba helper for custom use
    primaryColor,
    primaryRgba,
    successRgba,
    warningRgba,
    infoRgba,
    errorRgba,
  };
};
